/**
 * Shared parse/persist for planner calendar multi-filters (localStorage).
 * Values: 'all' → null; legacy single id string → [id]; JSON array → sorted unique ids.
 */

export function parseStoredMultiCalendarFilter(stored) {
  try {
    if (stored === null || stored === '' || stored === 'all') return null;
    if (typeof stored === 'string' && stored.startsWith('[')) {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return null;
      return parsed
        .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
        .filter((n) => !Number.isNaN(n));
    }
    const num = parseInt(stored, 10);
    return Number.isNaN(num) ? null : [num];
  } catch {
    return null;
  }
}

export function loadMultiCalendarFilterFromStorage(storageKey) {
  try {
    return parseStoredMultiCalendarFilter(localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

export function persistMultiCalendarFilter(storageKey, ids) {
  try {
    if (ids === null) {
      localStorage.setItem(storageKey, 'all');
      return;
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify([...new Set(ids)].sort((a, b) => a - b))
    );
  } catch {
    // ignore
  }
}

/** True when persisted multi-calendar filter compares equal (null vs array canonical). */
export function multiCalendarFiltersEqual(a, b) {
  if (a === null && b === null) return true;
  if ((a === null) !== (b === null)) return false;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
}

/**
 * Drops ids not present in current `calendars`, and collapses to `null` when selection covers all ids.
 */
export function coerceMultiCalendarFilter(value, calendars) {
  const ids = Array.isArray(calendars) ? calendars.map((c) => c.id) : [];
  const validSet = new Set(ids);
  if (!validSet.size) {
    // Calendars prop not populated yet — keep stored filter until scoped list exists.
    if (value === null) return null;
    return Array.isArray(value) ? value : null;
  }
  if (value === null) return null;
  if (!Array.isArray(value)) return null;
  const filtered = [...new Set(value.filter((id) => validSet.has(id)))].sort((a, b) => a - b);
  const allSorted = [...validSet].sort((a, b) => a - b);
  const coversAll =
    filtered.length === allSorted.length && allSorted.every((id, i) => id === filtered[i]);
  return coversAll ? null : filtered;
}

/** First selected id for modals; empty / all → null */
export function defaultCalendarIdFromMultiFilterStorage(stored) {
  try {
    if (stored === null || stored === '' || stored === 'all') return null;
    if (typeof stored === 'string' && stored.startsWith('[')) {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) return null;
      const n = typeof parsed[0] === 'number' ? parsed[0] : parseInt(String(parsed[0]), 10);
      return Number.isNaN(n) ? null : n;
    }
    const num = parseInt(stored, 10);
    return Number.isNaN(num) ? null : num;
  } catch {
    return null;
  }
}
